import { Action, Reducer } from 'redux';
import { AppThunkAction } from './';
import { get, post, remove } from "../fetch-interceptor";
import * as analytics from '../analytics';
import * as appInsights from '../appInsights';
import { UserState, ActionTypes as UserActionTypes, UserActions } from "./User";
import {
    contains as enumContains, CommonOperations, LicenseType, IPermissions, IServerPermissions, toPermissions, toServerPermissions,
    contains as permissionsContains, ResourceOperations
} from "./permissions";
import { FinancialsState, ActionTypes as FinancialsActionTypes, FinancialsActions } from "./FinancialsStore";
import { catchApiError, defaultCatch } from "./utils";
import { RouterAction, replace } from 'react-router-redux';
import { SourceType } from './ExternalEpmConnectStore';
import { ReportsNav } from '../components/utils/reporting';
import { baseUrl, cultureInfo, toDateTime } from '../components/utils/common';
import { IFieldMapping } from './integration/common';
import { GroupInfo, OrganizationInfo } from './integration/Office365Store';
import * as Notifications from "./NotificationsStore";
import { AuthProvider, AuthProvidersMap, Dictionary, EntityType, ProgressCalculationType, ServerEntityType, TaskType } from '../entities/common';
import { addOrUpdateOrRemove } from './services/storeHelper';
import { Richtext } from '../components/common/inputs/RichTextInput';
import { DayOfWeek } from 'office-ui-fabric-react';
// -----------------
// STATE - This defines the type of data maintained in the Redux store.

export class Tenant {
    [key: string]: string;
    companyName: string = '';
    companyAdress: string = '';
    companyWebSite: string = '';
    personName: string = '';
    personJobTitle: string = '';
    personEmail: string = '';
    personPhoneNumber: string = '';
    referralCode: string = '';
    plan: string = '';
    timezone: string = '';
}

export type GlobalSettings = {
    timezone: string;
    cultureName: string;
    firstDayOfWeek: DayOfWeek;
}

export type ResourcePlanningSettings = {
    resourcePlanningLevel: ServerEntityType
}

export type DefaultProjectSettings = {
    progressCalculationType: ProgressCalculationType;
    taskType: TaskType;
    effortDriven: boolean;
    calculateStartFinishDate: boolean;
    updateProgress: boolean;
    shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay?: boolean;
}

export enum PresentationModeSourceType {
    Generic = 0,
    ProjectStory = 1
}

export type PresentationModeSettings = {
    project: string | undefined;
    sourceType: PresentationModeSourceType;
}

export type DefaultPresentationModeSettings = {
    viewerPresentationMode: boolean;
    regularPresentationMode: boolean;
}

export type SyncSettings = {
    schedule: SyncSchedule,
    alertDelay: string | null,
    alertRecipientIds: string[]
}

export type SyncSchedule = {
    isPerpetual: boolean;
    days: any[];
    times: any[];
}

export type NotificationSchedule = {
    isUserNotificationsEnabled: boolean;
    dailySchedule: HealthReportingSchedule;
    weeklySchedule: HealthReportingSchedule;
}

export enum StatusCalculationTypes {
    Auto = 0x1,
    Manual = 0x2,

    AutoManual = Auto | Manual
}

export type HealthReportingSchedule = {
    days: any[];
    times: any[];
}

export type AzureADSyncSettings = {
    organization: OrganizationInfo;
    lastSyncTime?: string;
    lastSyncErrorMessage?: string;
    schedule: AzureADSyncSchedule;
    mapping: IFieldMapping[];
}

export type OktaAuthenticationSettings = {
    domain?: string;
    clientId?: string;
    clientSecret?: string;
}

export type AzureADSyncSchedule = {
    days: any[];
    times: any[];
}

export type AzureADUsersSyncSettings = {
    organization: OrganizationInfo;
    usersGroup: GroupInfo;
    teamMembersGroup: GroupInfo;
    isADUsersSyncEnabled: boolean;
    syncType: AdUsersSyncType;
    lastSyncTime?: string;
    lastSyncErrorMessage?: string;
    schedule: AzureADSyncSchedule;
}

type StyleSettings = {
    logoId: string | undefined;
    themeType: ThemeType;
}

export enum AdUsersSyncType {
    DoNotSendInvites,
    SendInvites,
    AutoActivation
}

export function checkIfADUsersSyncEnabled(tenant: TenantState) {
    return Subscription.contains(tenant.subscription, PPMFeatures.AzureADUsersSync) && !!tenant.adUsersSyncSettings?.isADUsersSyncEnabled;
}

export enum ProductType {
    Core = 1,
    Portfolio = 2,
    Product = 3,
    Work = 4,
    Resource = 5,
    Ideation = 6,
    Enterprise = 7,
    Once = 8,
}

export enum PaymentType {
    Card = 1,
    Direct = 2
}

export enum PaymentPeriod {
    Unknown = 0,
    Day = 1,
    Week = 2,
    Month = 3,
    Year = 4,
    Custom = 5
}

export namespace ProductType {
    const _map = {
        [ProductType.Portfolio]: {
            icon: "PPMXPortfolioPlan",
            title: "Portfolio"
        },
        [ProductType.Product]: {
            icon: "PPMXProductManagementPlan",
            title: "Product"
        },
        [ProductType.Core]: {
            icon: "PPMXCorePlan",
            title: "Core"
        },
        [ProductType.Work]: {
            icon: "PPMXCorePlan",
            title: "Project"
        },
        [ProductType.Resource]: {
            icon: 'PPMXResourcePlan',
            title: 'Resource'
        },
        [ProductType.Ideation]: {
            icon: 'PPMXIdeationPlan',
            title: 'Demand and Innovation Management'
        },
        [ProductType.Enterprise]: {
            icon: 'PPMXEnterprisePlan',
            title: 'Enterprise'
        },
        [ProductType.Once]: {
            icon: 'PPMXPortfolioPlan',
            title: 'Once',
        }
    }

    export function getIcoName(productType: ProductType): string | undefined {
        return _map[productType]?.icon;
    }

    export function getTitle(productType: ProductType): string | undefined {
        return _map[productType]?.title;
    }
}

export const showDemo = (productType: ProductType) => ![ProductType.Work, ProductType.Resource, ProductType.Once].includes(productType);
export const isAzureAdAvailable = (productType: ProductType) => ![ProductType.Work, ProductType.Resource, ProductType.Ideation, ProductType.Once].includes(productType);

export enum SubscriptionStatus {
    Disabled = 0,
    Active = 1,
    NotPaid = 2
}

export enum PPMIntegrations {
    None = 0,

    Financials = 1,
    VSTS = 4,
    Jira = 8,
    File = 16,
    Spo = 32,
    O365Planner = 64,
    O365User = 128,
    O365Group = 256,
    O365TeamsChannel = 512,
    MondayCom = 1024,
    Smartsheet = 2048,
    MPPFile = 4096,
    P4W = 8192,

    Collaborative = O365Group | O365TeamsChannel,
    O365 = O365Planner | O365User | O365Group | O365TeamsChannel,
    Syncable = VSTS | Jira | Spo | O365Planner | MondayCom | Smartsheet | P4W,
    All = Syncable | O365 | Financials | File | MPPFile
}

export class Integrations {
    private _integrations: PPMIntegrations;

    constructor(integrations: PPMIntegrations) {
        this._integrations = integrations;
    }

    public has = (required: PPMIntegrations) => {
        return enumContains(this._integrations, required);
    }

    public isDisabled = (sourceType: SourceType) => {
        const integration = this.map[sourceType]
        return !enumContains(this._integrations, integration);
    }

    public isEnabled = (sourceType: SourceType) => {
        const integration = this.map[sourceType];
        return enumContains(this._integrations, integration);
    }

    public getAvailable = (types: SourceType[], sourceInfos?: { type: SourceType }[]) => {
        if (sourceInfos) {
            const linked = sourceInfos.map(_ => _.type);
            return types.filter(_ => this.isEnabled(_) || ~linked.indexOf(_))
        }

        return types.filter(this.isEnabled);
    }

    private map: { [k: number]: PPMIntegrations } = {
        [SourceType.Ppmx]: PPMIntegrations.None,
        [SourceType.O365Group]: PPMIntegrations.O365Group,
        [SourceType.VSTS]: PPMIntegrations.VSTS,
        [SourceType.Jira]: PPMIntegrations.Jira,
        [SourceType.File]: PPMIntegrations.File,
        [SourceType.O365Planner]: PPMIntegrations.O365Planner,
        [SourceType.O365User]: PPMIntegrations.O365User,
        [SourceType.Spo]: PPMIntegrations.Spo,
        [SourceType.MondayCom]: PPMIntegrations.MondayCom,
        [SourceType.Smartsheet]: PPMIntegrations.Smartsheet,
        [SourceType.O365TeamsChannel]: PPMIntegrations.O365TeamsChannel,
        [SourceType.Slack]: PPMIntegrations.None,
        [SourceType.Confluence]: PPMIntegrations.None,
        [SourceType.MPPFile]: PPMIntegrations.MPPFile,
        [SourceType.P4W]: PPMIntegrations.P4W
    }
}

export enum PPMFeatures {
    None = "none",
    Insights = "insights",
    Prioritization = "prioritization",
    Ideation = "ideation",
    EmbeddedPowerBI = "embeddedpowerbi",
    PerpetualSync = "perpetualsync",
    ML = "ml",
    ResourceManagement = "resourcemanagement",
    OKR = "okr",
    Roadmap = "roadmap",
    API = "api",
    IntegrationAPI = "integrationapi",
    AzureADUsersSync = "azureaduserssync",
    ArchiveProjects = "archiveprojects",
    PortfolioManagement = "portfoliomanagement",
    AiInsights = "aiinsights",
    PartnersSupport = "partnerssupport",
    ProjectManagement = "projectmanagement",
    TimeTracking = "timetracking",
    TimeTrackingEnterprise = "timetrackingenterprise",
    TimesheetApprovals = "timesheetapprovals",
    StageStandardApprovals = "stagestandardapprovals",
    PrivateProjects = "privateprojects",
    ProcessCreation = "processcreation",
    RequestDemo = "requestdemo",
    UpgradeProposal = "upgradeproposal",
    CustomLogo = "customlogo",
    SubentityRollups = "subentityrollups",
    ProcessStageLayouts = "processstagelayouts",
    EmbeddedContent = "embeddedcontent",
    Extensions = "extensions",
    PresentationMode = "presentationmode",
    PresentationModeUltra = "presentationmodeultra",
    ProjectStoryCore = "projectstorycore",
    ProjectStoryUltra = "projectstoryultra",
    Suggestions = "suggestions",
    ResourceRoles = "resourceroles",
    AiAgents = "aiagents",
    AuditLog = "auditlog",
    OktaAuth = "oktaauth"
}

export namespace PPMFeatures {
    const _titleMap: Dictionary<string> = {
        [PPMFeatures.Roadmap]: "Roadmaps",
        [PPMFeatures.Insights]: "PPM Insights AI",
        [PPMFeatures.ML]: "Status Prediction AI",
        [PPMFeatures.ProjectManagement]: "Project Management",
        [PPMFeatures.PortfolioManagement]: "Portfolio Management",
    }

    export function getTitle(feature: PPMFeatures): string {
        return _titleMap[feature] ?? Object.entries(PPMFeatures).find(([_, v]) => v === feature)?.[0] ?? feature;
    }

    export function getFeaturesValues(): PPMFeatures[] {
        return Object.values(PPMFeatures) as PPMFeatures[];
    }

    export function combineFeatures(f1: PPMFeatures[], f2: PPMFeatures | PPMFeatures[]) {
        if (!Array.isArray(f2)) {
            return Array.from(new Set([...f1, f2]))
        }

        return Array.from(new Set([...f1, ...f2]))
    }

    export function extractFeatures(acc: PPMFeatures[], featureToRemove: PPMFeatures | PPMFeatures[]) {
        if (!Array.isArray(featureToRemove)) {
            return acc.filter(_ => _ !== featureToRemove)
        }

        const result = acc.filter(_ => !featureToRemove.includes(_));
        return result.length === 0 ? [PPMFeatures.None] : result;
    }

    export function isEmpty(features: PPMFeatures[]) {
        return features.length === 0
            || features.every(_ => _ === PPMFeatures.None);
    }
}

export interface Subscription {
    subscriptionId?: string;
    plan: string;
    endDate: string;
    productType: ProductType;
    paymentType: PaymentType;
    paymentPeriod: PaymentPeriod;
    isEnterprise: boolean;
    status: SubscriptionStatus;
    dataSyncCountLimit: number;
    gracePeriodDuration: number;
    isTrial: boolean;
    allFeatures: PPMFeatures[];
    features: PPMFeatures[];
    integrations: PPMIntegrations;
    viewersLimit: number;
    helpUrl?: string;
}

export namespace Subscription {
    const MAX_SUBSCRIPTION_PROCESSING_DURATION_DAYS = 1;
    const PORTFOLIO_MANAGEMENT_ENTITY_TYPES = [EntityType.Portfolio, EntityType.Program];

    export function isDirect(subscription: { paymentType: PaymentType }) {
        return subscription.paymentType === PaymentType.Direct;
    }

    export function isMinimalSupport(subscription: { productType: ProductType; }) {
        return subscription.productType === ProductType.Once;
    }

    export function isExpired(subscription: { endDate?: string, status: SubscriptionStatus }): boolean {
        const endDate = getEndDate(subscription);
        return !!endDate &&
            (subscription.status === SubscriptionStatus.NotPaid && endDate < new Date() // chargebee has already marked subscription as expired
                || endDate.addDays(MAX_SUBSCRIPTION_PROCESSING_DURATION_DAYS) < new Date());
    }

    export function contains(subscription: { features: PPMFeatures[] }, features: PPMFeatures | PPMFeatures[]): boolean {
        if (!subscription) {
            return false;
        }

        if (!Array.isArray(features)) {
            return subscription.features.includes(features);
        }

        return features.every(_ => subscription.features.includes(_));
    }

    export function containsAny(subscription: { features: PPMFeatures[] }, features: PPMFeatures[]): boolean {
        return subscription && features.some(_ => subscription.features.includes(_));
    }

    export function hasIntegration(subscription: { integrations: PPMIntegrations }, integration: PPMIntegrations): boolean {
        return subscription && enumContains(subscription.integrations, integration);
    }

    export function hasSyncableIntegration(subscription: { integrations: PPMIntegrations }): boolean {
        return subscription && (subscription.integrations & PPMIntegrations.Syncable) !== 0;
    }

    export function canViewInsights(subscription: { features: PPMFeatures[] }, user: UserState): boolean {
        return Subscription.contains(subscription, PPMFeatures.Insights)
            && enumContains(user.permissions.common, CommonOperations.InsightsView);
    }

    export function canViewOKR(subscription: { features: PPMFeatures[] }, user: UserState): boolean {
        return Subscription.contains(subscription, PPMFeatures.OKR)
            && permissionsContains(user.permissions.objective.global, ResourceOperations.Read);
    }

    export function getCreateEntityTypes(subscription: { features: PPMFeatures[], isEnterprise: boolean }, settings: DefaultProjectSettings | undefined, entityTypes: EntityType[]) {
        if (!Subscription.contains(subscription, PPMFeatures.PortfolioManagement)) {
            entityTypes = entityTypes.filter(_ => !PORTFOLIO_MANAGEMENT_ENTITY_TYPES.includes(_));
        }

        if (!Subscription.contains(subscription, PPMFeatures.ProjectManagement)) {
            entityTypes = entityTypes.filter(_ => _ !== EntityType.Project);
        }

        if (!Subscription.contains(subscription, PPMFeatures.PrivateProjects)) {
            entityTypes = entityTypes.filter(_ => _ !== EntityType.PrivateProject);
        }

        return entityTypes;
    }

    export function canODataView(user: UserState): boolean {
        return enumContains(user.permissions.common, CommonOperations.ODataView);
    }

    function getEndDate(subscription: { endDate?: string }): Date | undefined {
        return subscription.endDate ? toDateTime(subscription.endDate) : undefined;
    }
}

export type IsRouteAvailable = (subscription: Subscription, user: UserState, settings: RouteAvailabilitySettings) => boolean;

export type RouteAvailabilitySettings = {
    newTimeTrackingEnabled: boolean;
    timesheetApprovalsEnabled: boolean;
}

export const ToRouteAvailabilitySettings = (tenant: TenantState): RouteAvailabilitySettings => {
    return {
        newTimeTrackingEnabled: tenant.timeTracking?.globalSettings.newTimeTrackingEnabled || false,
        timesheetApprovalsEnabled: tenant.timeTracking?.globalSettings.timesheetApprovalsEnabled || false
    }
}

interface RoutesAvailabilityMap {
    [name: string]: IsRouteAvailable;
}

const createAvailabilityMap = <M extends RoutesAvailabilityMap>(things: M) => things

export const RoutesAvailability = createAvailabilityMap({
    Automation: (subscription, user) => Subscription.containsAny(subscription, [PPMFeatures.API, PPMFeatures.IntegrationAPI]) && user.license === LicenseType.Regular,
    ArchivedProjects: (subscription, _) => Subscription.contains(subscription, PPMFeatures.ArchiveProjects),
    Audit: (subscription, _) => Subscription.contains(subscription, PPMFeatures.AuditLog),
    Billing: (subscription, _) => !!subscription?.subscriptionId,
    StatusSettings: (subscription, user) => Subscription.containsAny(subscription, [PPMFeatures.AiInsights, PPMFeatures.PortfolioManagement, PPMFeatures.ProjectManagement, PPMFeatures.OKR])
        && enumContains(user.permissions.common, CommonOperations.AdministrateView),
    TenantSettings: (_, user) => enumContains(user.permissions.common, CommonOperations.ScheduleView),
    Users: (_, user) => enumContains(user.permissions.common, CommonOperations.UserView),
    Challenges: (subscription, _) => Subscription.contains(subscription, PPMFeatures.Ideation),
    Priorities: (subscription, _) => Subscription.contains(subscription, PPMFeatures.Prioritization),
    Objectives: Subscription.canViewOKR,
    Roadmaps: (subscription, _) => Subscription.contains(subscription, PPMFeatures.Roadmap),
    Portfolios: (subscription, _) => Subscription.contains(subscription, PPMFeatures.PortfolioManagement),
    Programs: (subscription, _) => Subscription.contains(subscription, PPMFeatures.PortfolioManagement),
    Insights: Subscription.canViewInsights,
    ResourcePlan: (subscription, _) => Subscription.contains(subscription, PPMFeatures.ResourceManagement)
        && Subscription.containsAny(subscription, [PPMFeatures.PortfolioManagement, PPMFeatures.ProjectManagement]),
    Projects: (subscription, _) => Subscription.contains(subscription, PPMFeatures.ProjectManagement),
    MySpace: (subscription, _) => Subscription.containsAny(subscription, [PPMFeatures.PortfolioManagement, PPMFeatures.ProjectManagement]),
    ProcessManagement: (_, user) => enumContains(user.permissions.common, CommonOperations.AdministrateView),
    TimeTrackingtSettings: (subscription, user, settings) => settings.newTimeTrackingEnabled
        && enumContains(user.permissions.common, CommonOperations.TimeTrackingView)
        && Subscription.containsAny(subscription, [PPMFeatures.TimeTracking, PPMFeatures.TimeTrackingEnterprise]),
    MyTime: (subscription, user, settings) => settings.newTimeTrackingEnabled
        && Subscription.containsAny(subscription, [PPMFeatures.TimeTracking, PPMFeatures.TimeTrackingEnterprise]),
    ReportedTime: (subscription, user, settings) => settings.newTimeTrackingEnabled
        && Subscription.containsAny(subscription, [PPMFeatures.TimeTracking, PPMFeatures.TimeTrackingEnterprise]),
    ApprovalCenter: (subscription, user, settings) =>
        (settings.timesheetApprovalsEnabled && Subscription.contains(subscription, PPMFeatures.TimesheetApprovals))
        || Subscription.contains(subscription, PPMFeatures.StageStandardApprovals),
    CalendarManagement: (subscription, user, settings) => Subscription.contains(subscription, PPMFeatures.PortfolioManagement),
})

export interface Insights {
    projectWarningsCalculationDisabled: boolean;
    programWarningsCalculationDisabled: boolean;
    portfolioWarningsCalculationDisabled: boolean;
    keyDate: CalculationSettings<GenericCalculationThresholds>;
    task: CalculationSettings<GenericCalculationThresholds>;
    project: CalculationSettings<ProjectStatusCalculationThresholds>;
    program: CalculationSettings<PortfolioOrProgramStatusCalculationThresholds>;
    portfolio: CalculationSettings<PortfolioOrProgramStatusCalculationThresholds>;
    keyResult: CalculationSettings<GenericCalculationThresholds>;
}

export const enum AIType {
    Ppmx = 0,
    OpenAI = 1,
    AzureOpenAI = 2,
}

export interface AiInsightsSettings {
    type: AIType;
    apiKey?: string,
    modelName?: string
    endpointUrl?: string,
    temperature: number;
}

export interface ICalculationSettings {
    statusCalculation: StatusCalculationTypes;
    manualDescription?: boolean;
}

export interface CalculationSettings<T> extends ICalculationSettings {
    thresholds: T;
}

export interface GenericCalculationThresholds {
    minMax: MinMax<number | null>;
}
export interface ProjectStatusCalculationThresholds {
    lateTasks: MinMax<number | null>;
    lateKeyDates: MinMax<number | null>;
    resourcesWithLateTasks: MinMax<number | null>;
    overdueIssues: MinMax<number | null>;
    overdueRisks: MinMax<number | null>;
    costOverrun: MinMax<number | null>;
    effortsOverrun: MinMax<number | null>;
    durationOverrun: MinMax<number | null>;
}

export interface PortfolioOrProgramStatusCalculationThresholds {
    costOverrun: MinMax<number | null>;
    lateKeyDates: MinMax<number | null>;
}

export type MinMax<T> = { min: T, max: T }

export interface IReportingInfo {
    odataUrl: string;
    sampleUrl?: string;
    error?: string;
    portfolioReports: ReportsNav;
    portfoliosReports: ReportsNav;
    programReports: ReportsNav;
    programsReports: ReportsNav;
    projectReports: ReportsNav;
    projectsReports: ReportsNav;
    resourceReports: ReportsNav;
    resourcesReports: ReportsNav;
    challengesReports: ReportsNav;
    objectivesReports: ReportsNav;
    pbixReports?: IPbixReports[]
}

interface IPbixReports {
    name: string;
    version: string;
}

export interface SecuritySettings {
    isAutoRegistrationDisabled: boolean;
    allowAutoRegistration: boolean;
    allowEveryoneToInviteUsers: boolean;
    allowedAuthProviders: AuthProvider[];
    defaultAuthProvider: AuthProvider;
    okta?: OktaSecuritySettings;
}

export interface OktaSecuritySettings {
    domain?: string;
    clientId?: string;
    clientSecret?: string;
    authSchemeName?: string;
}

export enum ThemeType {
    Dark,
    Light,
    Solid,
}

export interface IDefaultPermissions<T> {
    regular: T;
    viewer: T;
}

export interface TimeTrackingSettings {
    globalSettings: TimeTrackingGlobalSettings;
    approvalReminderSettings: TimesheetApprovalReminderSettings;
    administrativeCategories: TimeTrackingAdministrativeCategory[];
}

export interface TimeTrackingGlobalSettings {
    newTimeTrackingEnabled: boolean;

    calculateCompletedWorkBasedOnReportedTime: boolean;
    calculateResourcePlanActualsBasedOnReportedTime: boolean;

    minReportingDurationMinutes: number;
    reportingIncrementMinutes: number;

    costTypeSource: ServerEntityType;
    billableSource: ServerEntityType;

    enableEditingBillable: boolean;
    enableEditingCostType: boolean;

    enabledAssignmentSmartSuggestions: boolean;
    enablaResourcePlanSmartSuggestion: boolean;
    enableO365Connector: boolean;

    enableReportingOnCustomTimeTrackingEntries: boolean;
    defaultCustomTimeEntryName: string;
    enableReportingOnNotAssignedTasks: boolean;

    enableExternalSuggestions: boolean;
    timesheetApprovalsEnabled: boolean;
    syncOnlyApprovedTime: boolean;
    allowFutureTimeSubmission: boolean;
    allowManagersToRecallApprovedRequests: boolean;
    delegationModeEnabled: boolean;
}

export enum ReminderFrequency {
    DoNotSend = 0,
    Daily = 1,
    Every3Days = 3,
    Weekly = 7
}

export interface TimesheetApprovalReminderSettings {
    approverReminderFrequency: ReminderFrequency;
    overdueApprovalReminderFrequency: ReminderFrequency;
    approvalRequestOverdueThresholdDays: number;
    timesheetSubmissionReminderFrequency: ReminderFrequency;
    timesheetSubmissionReminderStartDays: number;
    overdueTimesheetReminderFrequency: ReminderFrequency;
    timesheetOverdueThresholdDays: number;
    lastSyncTime?: string;
    nextSyncTime?: string;
    schedule: {
        days: any[];
        times: any[];
    };
}

interface TimeTrackingAdministrativeCategoryBase {
    title: string;
    description: Richtext;
    isEnabled: boolean;
    color: string;
    type: TimeTrackingAdministrativeCategoryType
}

export enum TimeTrackingAdministrativeCategoryType {
    NonProjectTime,
    NotWorkingTime
}

export const TimeTrackingAdministrativeCategoryTypeLabelsMap = {
    [TimeTrackingAdministrativeCategoryType.NonProjectTime]: "Non-project Time",
    [TimeTrackingAdministrativeCategoryType.NotWorkingTime]: "Non-working Time"
};

export interface TimeTrackingAdministrativeCategory extends TimeTrackingAdministrativeCategoryBase {
    id: string;
}

export interface TimeTrackingAdministrativeCategorySave extends TimeTrackingAdministrativeCategoryBase {
    id?: string;
}

export function getDefaultPermissions<T>(license: LicenseType, defaultPermissions?: IDefaultPermissions<T>) {
    return license === LicenseType.Regular
        ? defaultPermissions?.regular
        : license === LicenseType.Viewer
            ? defaultPermissions?.viewer
            : undefined;
}

export interface StorageInfo {
    capacity: number;
    usage: number;
    maxFileSize: number;
    usageWarningPercent: number;
}

export interface TenantState {
    region: string;
    id: string;
    externalId: string;
    externalAuthProvider?: AuthProvider;
    companyName: string;
    isReadOnly: boolean;
    isDemoDataApplied: boolean;
    name: string;
    url: string,
    styleSettings: StyleSettings;
    globalSettings?: GlobalSettings;
    resourcePlanningSettings?: ResourcePlanningSettings;
    syncSettings?: SyncSettings;
    healthReportingSettings?: NotificationSchedule;
    adSyncSettings?: AzureADSyncSettings;
    adUsersSyncSettings?: AzureADUsersSyncSettings;
    security?: SecuritySettings;
    reporting: IReportingInfo;
    insights: Insights;
    aiInsights: AiInsightsSettings;
    subscription: Subscription;
    isProcessing: boolean;
    isLoading: boolean;
    defaultPermissions?: IDefaultPermissions<IPermissions>;
    defaultProjectSettings?: DefaultProjectSettings;
    timeTracking: TimeTrackingSettings;
    presentationModeSettings?: PresentationModeSettings;
    defaultPresentationModeSettings?: DefaultPresentationModeSettings;
    storage: StorageInfo;
}

export enum ActionTypes {
    REGISTER_TENANT = 'REGISTER_TENANT',
    UPDATE_TENANT = 'UPDATE_TENANT',
    UPDATE_AVAILABLE_TENANTS = 'UPDATE_AVAILABLE_TENANTS',
    UPDATING_TENANT = 'UPDATING_TENANT',
    UPDATE_SCHEDULE = 'UPDATE_SCHEDULE',
    UPDATE_AD_SYNC_SETTINGS = 'UPDATE_AD_SYNC_SETTINGS',
    UPDATE_AD_USERS_SYNC_SETTINGS = 'UPDATE_AD_USERS_SYNC_SETTINGS',
    UPDATE_DEFAULT_PROJECT_SETTINGS = 'UPDATE_DEFAULT_PROJECT_SETTINGS',
    UPDATE_PRESENTATION_MODE_SETTINGS = 'UPDATE_UPDATE_PRESENTATION_MODE_SETTINGS',
    UPDATE_SETTINGS = 'UPDATE_SETTINGS',
    UPDATE_EXPORTIMPORT_SETTINGS = 'UPDATE_EXPORTIMPORT_SETTINGS',
    UPDATE_RESOURCE_PLANNING_SETTINGS = 'UPDATE_RESOURCE_PLANNING_SETTINGS',
    UPDATE_SECURITY = 'UPDATE_SECURITY',
    UPDATE_IS_DEMO_DATA_APPLIED = 'UPDATE_IS_DEMO_DATA_APPLIED',
    UPDATE_SUBSCRIPTION = 'UPDATE_SUBSCRIPTION',
    UPDATE_INSIGHTS = 'UPDATE_INSIGHTS',
    UPDATE_AI_INSIGHTS = 'UPDATE_AI_INSIGHTS',
    RECEIVED_REPORTING_INFO = 'RECEIVED_REPORTING_INFO',
    DEFAULT_PERMISSIONS_LOADED = 'DEFAULT_PERMISSIONS_LOADED',
    DEFAULT_PRESENTATION_MODE_LOADED = 'DEFAULT_PRESENTATION_MODE_LOADED',
    BEGIN_LOADING = "TENANT_BEGIN_LOADING",
    LINK_TENANT = "LINK_TENANT",
    UPDATE_LOGO = "UPDATE_LOGO",
    SET_THEME = "SET_THEME",
    UPDATE_TIMETRACKING_GLOBAL_SETTINGS = 'UPDATE_TIMETRACKING_GLOBAL_SETTINGS',
    UPDATE_TIMETRACKING_ADMINISTRATIVE_CATEGORY = 'CREATE_TIMETRACKING_ADMINISTRATIVE_CATEGORY',
    UPDATE_TIMETRACKING_APPROVAL_REMINDER_SETTINGS = 'UPDATE_TIMETRACKING_APPROVAL_REMINDER_SETTINGS',
    UPDATE_STORAGE_INFO = "UPDATE_STORAGE_INFO",
}

type RegisterTenantAction = { type: ActionTypes.REGISTER_TENANT }
type UpdateTenantAction = { type: ActionTypes.UPDATE_TENANT, tenant: TenantState }
type LinkTenantAction = { type: ActionTypes.LINK_TENANT, externalId: string, externalAuthProvider: AuthProvider }
type UpdateIsDemoDataApplied = { type: ActionTypes.UPDATE_IS_DEMO_DATA_APPLIED, isDemoDataApplied: true }
type UpdatingTenantAction = { type: ActionTypes.UPDATING_TENANT, isProcessing?: boolean; }
type UpdateSettingsAction = { type: ActionTypes.UPDATE_SETTINGS, globalSettings: GlobalSettings }
type UpdateResourcePlanningSettingsAction = { type: ActionTypes.UPDATE_RESOURCE_PLANNING_SETTINGS, resourcePlanningSettings: ResourcePlanningSettings }
type UpdateScheduleAction = { type: ActionTypes.UPDATE_SCHEDULE, syncSchedule: SyncSchedule, alertDelay: string | null, alertRecipientIds: string[], notificationSchedule: NotificationSchedule }
type UpdateADSyncSettingsAction = { type: ActionTypes.UPDATE_AD_SYNC_SETTINGS, adSyncSettings: AzureADSyncSettings }
type UpdateADUsersSyncSettingsAction = { type: ActionTypes.UPDATE_AD_USERS_SYNC_SETTINGS, adUsersSyncSettings: AzureADUsersSyncSettings }
type UpdateDefaultProjectSettingsAction = { type: ActionTypes.UPDATE_DEFAULT_PROJECT_SETTINGS, defaultProjectSettings: DefaultProjectSettings }
type UpdatePresentationModeSettingsAction = { type: ActionTypes.UPDATE_PRESENTATION_MODE_SETTINGS, presentationModeSettings: PresentationModeSettings }
type UpdateSecurityAction = { type: ActionTypes.UPDATE_SECURITY, security: SecuritySettings }
type DefaultPermissionsLoadedAction = { type: ActionTypes.DEFAULT_PERMISSIONS_LOADED; permissions: IDefaultPermissions<IServerPermissions>; }
type DefaultPresentationModeLoadedAction = { type: ActionTypes.DEFAULT_PRESENTATION_MODE_LOADED; settings: DefaultPresentationModeSettings; }
type UpdateSubscriptionAction = { type: ActionTypes.UPDATE_SUBSCRIPTION, subscription: Subscription }
type UpdateInsightsAction = { type: ActionTypes.UPDATE_INSIGHTS, insights: Insights }
type UpdateAiInsightsAction = { type: ActionTypes.UPDATE_AI_INSIGHTS, aiInsights: AiInsightsSettings }
type BeginLoadingActionAction = { type: ActionTypes.BEGIN_LOADING }
type UpdateLogoAction = { type: ActionTypes.UPDATE_LOGO, logoId: string | undefined }
type UpdateThemeAction = { type: ActionTypes.SET_THEME, themeType: ThemeType }
export type UpdateStorageAction = { type: ActionTypes.UPDATE_STORAGE_INFO, storage: StorageInfo }

export type ReceivedReportingInfoAction = { type: ActionTypes.RECEIVED_REPORTING_INFO; info: IReportingInfo; }
type UpdateTimeTrackingGlobalSettingsAction = { type: ActionTypes.UPDATE_TIMETRACKING_GLOBAL_SETTINGS; settings: TimeTrackingGlobalSettings };
type UpdateTimeTrackingAdministrativeCategory = {
    type: ActionTypes.UPDATE_TIMETRACKING_ADMINISTRATIVE_CATEGORY;
    addOrUpdate?: TimeTrackingAdministrativeCategory[];
    remove?: string[];
};
type UpdateTimesheetApprovalReminderSettingsAction = {
    type: ActionTypes.UPDATE_TIMETRACKING_APPROVAL_REMINDER_SETTINGS;
    settings: TimesheetApprovalReminderSettings;
};

export type TenantActions = RegisterTenantAction
    | UpdateTenantAction
    | UpdatingTenantAction
    | UpdateScheduleAction
    | UpdateADSyncSettingsAction
    | UpdateADUsersSyncSettingsAction
    | UpdateDefaultProjectSettingsAction
    | UpdateSettingsAction
    | UpdateResourcePlanningSettingsAction
    | UpdateSecurityAction
    | DefaultPermissionsLoadedAction
    | DefaultPresentationModeLoadedAction
    | UpdateSubscriptionAction
    | UpdateInsightsAction
    | UpdateAiInsightsAction
    | ReceivedReportingInfoAction
    | UpdateIsDemoDataApplied
    | BeginLoadingActionAction
    | LinkTenantAction
    | UpdateLogoAction
    | UpdateThemeAction
    | UpdateTimeTrackingGlobalSettingsAction
    | UpdateTimeTrackingAdministrativeCategory
    | UpdateTimesheetApprovalReminderSettingsAction
    | UpdatePresentationModeSettingsAction
    | UpdateStorageAction;

export const actionCreators = {
    registerTenant: (tenant: Tenant & { plan: string }): AppThunkAction<TenantActions | UserActions | FinancialsActions | RouterAction> => (dispatch, getState) => {
        const tenantState = getState().tenant;
        if (!(tenantState && tenantState.id)) {
            post<{ tenant: TenantState, user: UserState, financials: FinancialsState }>(`api/tenant/register`, { ...tenant, url: baseUrl.value.split('@')[1] })
                .then(data => {
                    analytics.identifyAndGroup(data.user, data.tenant);
                    appInsights.identify(data.user, data.tenant);
                    dispatch({ type: ActionTypes.UPDATE_TENANT, tenant: data.tenant });
                    dispatch({ type: UserActionTypes.UPDATE_USER, user: data.user });
                    dispatch({ type: FinancialsActionTypes.UPDATE_FINANCIALS, financials: data.financials });
                    dispatch(replace({ pathname: '/quickstart' }));
                    analytics.gtag('event', 'tenant_created', { userSignInSystem: AuthProvidersMap[data.user.signInAuthProvider].friendlyName });
                    analytics.track('Register Tenant', {
                        tenantId: data.tenant.id,
                        tenantName: data.tenant.name,
                        tenantCompanyName: data.tenant.companyName,
                        userSignInSystem: AuthProvidersMap[data.user.signInAuthProvider].friendlyName,
                        plan: tenant.plan
                    });
                })
                .catch(defaultCatch(dispatch));

            dispatch({ type: ActionTypes.REGISTER_TENANT });
        }
    },
    updateSyncSchedule: (syncSchedule: SyncSchedule,
        alertDelay: string | null,
        alertRecipientIds: string[],
        notificationSchedule: NotificationSchedule): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<{
            syncSchedule: SyncSchedule, alertDelay: string | null, alertRecipientIds: string[],
            notificationSchedule: NotificationSchedule
        }>(`api/schedule/settings`,
            { syncSchedule, alertDelay, alertRecipientIds, notificationSchedule })
            .then(data => {
                dispatch({
                    type: ActionTypes.UPDATE_SCHEDULE,
                    syncSchedule: data.syncSchedule,
                    alertDelay: data.alertDelay,
                    alertRecipientIds: data.alertRecipientIds,
                    notificationSchedule: data.notificationSchedule
                });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    loadADSyncSettings: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        get<AzureADSyncSettings>(`api/adsync/settings`)
            .then(data => dispatch({ type: ActionTypes.UPDATE_AD_SYNC_SETTINGS, adSyncSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateADSyncSettings: (schedule: AzureADSyncSchedule, mapping: IFieldMapping[], organization: OrganizationInfo): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<AzureADSyncSettings>(`api/adsync/settings`, { schedule, mapping, organization })
            .then(data => dispatch({ type: ActionTypes.UPDATE_AD_SYNC_SETTINGS, adSyncSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    runADSync: (): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<GlobalSettings>(`api/adsync/run`, {})
            .catch(defaultCatch(dispatch));
        dispatch(Notifications.actionCreators.pushNotification({ message: "Azure Active Directory synchronization is in progress", type: Notifications.NotificationType.Success }));
    },
    loadADUsersSyncSettings: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        get<AzureADUsersSyncSettings>(`api/adUsersSync/settings`)
            .then(data => dispatch({ type: ActionTypes.UPDATE_AD_USERS_SYNC_SETTINGS, adUsersSyncSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateADUsersSyncSettings: (schedule: AzureADSyncSchedule, organization: OrganizationInfo, usersGroup: GroupInfo, teamMembersGroup: GroupInfo,
        isADUsersSyncEnabled: boolean, syncType: AdUsersSyncType): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<AzureADUsersSyncSettings>(`api/adUsersSync/settings`, { schedule, organization, usersGroup, teamMembersGroup, isADUsersSyncEnabled, syncType })
            .then(data => dispatch({ type: ActionTypes.UPDATE_AD_USERS_SYNC_SETTINGS, adUsersSyncSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    runADUsersSync: (): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<GlobalSettings>(`api/adUsersSync/run`, {})
            .catch(defaultCatch(dispatch));
        dispatch(Notifications.actionCreators.pushNotification({
            message: "Azure Active Directory users synchronization is in progress",
            type: Notifications.NotificationType.Success
        }));
    },
    loadPresentationModeSettings: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        get<PresentationModeSettings>('api/presentationMode/settings')
            .then(data => dispatch({ type: ActionTypes.UPDATE_PRESENTATION_MODE_SETTINGS, presentationModeSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updatePresentationModeSettings: (presentationModeSettings: PresentationModeSettings): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<PresentationModeSettings>(`api/presentationMode/settings`, { ...presentationModeSettings })
            .then(data => dispatch({ type: ActionTypes.UPDATE_PRESENTATION_MODE_SETTINGS, presentationModeSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    loadDefaultProjectSettings: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        get<DefaultProjectSettings>('api/defaultProject/settings')
            .then(data => dispatch({ type: ActionTypes.UPDATE_DEFAULT_PROJECT_SETTINGS, defaultProjectSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateDefaultProjectSettings: (defaultProjectSettings: Partial<DefaultProjectSettings>): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<DefaultProjectSettings>(`api/defaultProject/settings`, { ...defaultProjectSettings })
            .then(data => dispatch({ type: ActionTypes.UPDATE_DEFAULT_PROJECT_SETTINGS, defaultProjectSettings: data }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateGlobalSettings: (timezone?: string, cultureName?: string, firstDayOfWeek?: DayOfWeek): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<GlobalSettings>(`api/global/settings`, { timezone, cultureName, firstDayOfWeek })
            .then(data => { dispatch({ type: ActionTypes.UPDATE_SETTINGS, globalSettings: data }); })
            .catch(defaultCatch(dispatch));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateResourcePlanningSettings: (resourcePlanningLevel: EntityType, callback?: () => void): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<ResourcePlanningSettings>(`api/resourcePlanning/settings`, { resourcePlanningLevel })
            .then(data => {
                dispatch({ type: ActionTypes.UPDATE_RESOURCE_PLANNING_SETTINGS, resourcePlanningSettings: data });
                callback?.();
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    applyDemoData: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<GlobalSettings>(`api/tenant/applyDemoData`, {})
            .then(data => { dispatch({ type: ActionTypes.UPDATE_IS_DEMO_DATA_APPLIED, isDemoDataApplied: true }); })
            .catch(defaultCatch(dispatch));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateSecurity: (security: SecuritySettings): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<SecuritySettings>(`api/security`, security)
            .then(data => { dispatch({ type: ActionTypes.UPDATE_SECURITY, security: data }); })
            .catch(defaultCatch(dispatch));
    },
    updateOktaSettings: (settings: OktaAuthenticationSettings): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<SecuritySettings>(`api/okta/settings`, settings)
            .then(data => { dispatch({ type: ActionTypes.UPDATE_SECURITY, security: data }); })
            .catch(defaultCatch(dispatch));
    },
    loadDefaultPermissions: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.BEGIN_LOADING });

        get<IDefaultPermissions<IServerPermissions>>('api/security/permissions')
            .then(_ => dispatch({ type: ActionTypes.DEFAULT_PERMISSIONS_LOADED, permissions: _ }))
            .catch(defaultCatch(dispatch));
    },
    updateDefaultPermissions: (license: LicenseType, permissions: IPermissions): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<IDefaultPermissions<IServerPermissions>>('api/security/permissions', { license, permissions: toServerPermissions(permissions) })
            .then(_ => dispatch({ type: ActionTypes.DEFAULT_PERMISSIONS_LOADED, permissions: _ }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    loadDefaultPresentationModeSettings: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.BEGIN_LOADING });
        get<DefaultPresentationModeSettings>('api/security/presentationMode')
            .then(_ => dispatch({ type: ActionTypes.DEFAULT_PRESENTATION_MODE_LOADED, settings: _ }))
            .catch(defaultCatch(dispatch));
    },
    updateDefaultPresentationModeSettings: (settings: DefaultPresentationModeSettings): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<DefaultPresentationModeSettings>('api/security/presentationMode', settings)
            .then(_ => dispatch({ type: ActionTypes.DEFAULT_PRESENTATION_MODE_LOADED, settings: _ }))
            .catch(defaultCatch(dispatch));
        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateSubscription: (subscription: Subscription): AppThunkAction<TenantActions> => (dispatch, getState) => {
        dispatch({ type: ActionTypes.UPDATE_SUBSCRIPTION, subscription: subscription });
    },
    updateInsights: (insights: Insights): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<Insights>(`api/insights/settings`, insights)
            .then(data => {
                dispatch({ type: ActionTypes.UPDATE_INSIGHTS, insights: data });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    linkToO365Organization: (connectionId: string): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<{ externalId: string, externalAuthProvider: AuthProvider }>(`api/tenant/linkToO365Organization`, { connectionId })
            .then(data => { dispatch({ type: ActionTypes.LINK_TENANT, ...data }); })
            .catch(catchApiError(_ => {
                dispatch(Notifications.actionCreators
                    .pushNotification({ message: `Failed to link. ${_ || ""}`, type: Notifications.NotificationType.Error }))
            }));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    linkToOktaOrganization: (): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<{ externalId: string, externalAuthProvider: AuthProvider, security: SecuritySettings }>(`api/tenant/linkToOktaOrganization`, {})
            .then(data => {
                dispatch({ type: ActionTypes.LINK_TENANT, ...data });
                dispatch({ type: ActionTypes.UPDATE_SECURITY, security: data.security });
            })
            .catch(catchApiError(_ => {
                dispatch(Notifications.actionCreators
                    .pushNotification({ message: `Failed to link. ${_ || ""}`, type: Notifications.NotificationType.Error }))
            }));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    updateAiInsightsSettings: (aiInsights: AiInsightsSettings): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<AiInsightsSettings>(`api/ai/insights/settings`, aiInsights)
            .then(data => {
                dispatch({ type: ActionTypes.UPDATE_AI_INSIGHTS, aiInsights: data });
            })
            .catch(defaultCatch(dispatch));

        dispatch({ type: ActionTypes.UPDATING_TENANT });
    },
    uploadLogo: (logo: File): AppThunkAction<TenantActions> => (dispatch, getState) => {
        const data = new FormData();
        data.set('image', logo);

        post<{ imageId: string }>(`api/style/logo`, data)
            .then(_ => dispatch({ type: ActionTypes.UPDATE_LOGO, logoId: _.imageId }))
            .catch(defaultCatch(dispatch));
    },
    removeLogo: (): AppThunkAction<TenantActions> => (dispatch, getState) => {
        remove<void>(`api/style/logo`)
            .then(_ => dispatch({ type: ActionTypes.UPDATE_LOGO, logoId: undefined }))
            .catch(defaultCatch(dispatch));
    },
    changeTheme: (themeType: ThemeType): AppThunkAction<TenantActions> => (dispatch, getState) => {
        post<StyleSettings>(`api/style/theme`, { themeType })
            .then(_ => dispatch({ type: ActionTypes.SET_THEME, themeType: _.themeType }))
            .catch(defaultCatch(dispatch));
    },
    createOrUpdateTimeTrackingAdministrativeCategory:
        (
            category: TimeTrackingAdministrativeCategorySave
        ): AppThunkAction<TenantActions> =>
            (dispatch, getState) => {
                post<any>(`api/timetracking/administrative`, category)
                    .then(data => {
                        dispatch({
                            type: ActionTypes.UPDATE_TIMETRACKING_ADMINISTRATIVE_CATEGORY,
                            addOrUpdate: [data]
                        });
                    })
                    .catch(defaultCatch(dispatch));

                dispatch({ type: ActionTypes.UPDATING_TENANT });
            },
    deleteTimeTrackingAdministrativeCategory:
        (categoryId: string): AppThunkAction<TenantActions | Notifications.KnownAction> =>
            (dispatch, getState) => {
                remove<any>(`api/timetracking/administrative/${categoryId}`)
                    .then(data => {
                        dispatch({
                            type: ActionTypes.UPDATE_TIMETRACKING_ADMINISTRATIVE_CATEGORY,
                            remove: [categoryId]
                        });
                    })
                    .catch(catchApiError(_ => {
                        dispatch(Notifications.actionCreators
                            .pushNotification({ message: _, type: Notifications.NotificationType.Error }))
                    }));

                dispatch({ type: ActionTypes.UPDATING_TENANT });
            },
    updateTimeTrackingSettings:
        (settings: Partial<TimeTrackingGlobalSettings>, callback?: () => void): AppThunkAction<TenantActions | Notifications.KnownAction> =>
            (dispatch, getState) => {
                post<TimeTrackingGlobalSettings>(`api/timetracking/settings`, settings)
                    .then(data => {
                        dispatch({
                            type: ActionTypes.UPDATE_TIMETRACKING_GLOBAL_SETTINGS,
                            settings: data
                        });

                        callback?.();
                    })
                    .catch(catchApiError(_ => {
                        dispatch(Notifications.actionCreators
                            .pushNotification({ message: _, type: Notifications.NotificationType.Error }))
                    }));

                dispatch({ type: ActionTypes.UPDATING_TENANT });
            },
    updateTimesheetApprovalReminderSettings:
        (settings: Partial<TimesheetApprovalReminderSettings>): AppThunkAction<TenantActions | Notifications.KnownAction> =>
            (dispatch, getState) => {
                post<TimesheetApprovalReminderSettings>(`api/timetracking/approval-reminder-settings`, settings)
                    .then(data => {
                        dispatch({
                            type: ActionTypes.UPDATE_TIMETRACKING_APPROVAL_REMINDER_SETTINGS,
                            settings: data
                        });
                    })
                    .catch(catchApiError(_ => {
                        dispatch(Notifications.actionCreators
                            .pushNotification({ message: _, type: Notifications.NotificationType.Error }))
                    }));

                dispatch({ type: ActionTypes.UPDATING_TENANT });
            },
    runTaskCompletedWorkRecalculation: (): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<GlobalSettings>(`/api/timetracking/sync/completed-work`, {})
            .catch(defaultCatch(dispatch));
        dispatch(Notifications.actionCreators.pushNotification({
            message: "Tasks Completed Work recalculation is in progress",
            type: Notifications.NotificationType.Success
        }));
    },
    updateFeatures: (features: PPMFeatures[], callback: () => void): AppThunkAction<TenantActions> => 
        (dispatch, getState) => {
            post<{ features: PPMFeatures[], security: SecuritySettings }>(`api/tenant/features`, { features: features })
                .then(data => {
                    const subscription = getState().tenant.subscription;
                    dispatch({ type: ActionTypes.UPDATE_SUBSCRIPTION, subscription: { ...subscription, features: data.features } });
                    dispatch({ type: ActionTypes.UPDATE_SECURITY, security: data.security });
                    dispatch({ type: ActionTypes.UPDATING_TENANT, isProcessing: false });
                    callback();
                })
                .catch(defaultCatch(dispatch));

            dispatch({ type: ActionTypes.UPDATING_TENANT });
        },
    runResourcePlanActualsRecalculation: (): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<GlobalSettings>(`/api/timetracking/sync/resource-plan`, {})
            .catch(defaultCatch(dispatch));
        dispatch(Notifications.actionCreators.pushNotification({
            message: "Resource Plan Actuals recalculation is in progress",
            type: Notifications.NotificationType.Success
        }));
    },
    runSyncOnlyApprovedTimeRecalculation: (): AppThunkAction<TenantActions | Notifications.KnownAction> => (dispatch, getState) => {
        post<GlobalSettings>(`/api/timetracking/sync/approval-required-time`, {})
            .catch(defaultCatch(dispatch));
        dispatch(Notifications.actionCreators.pushNotification({
            message: "Reported time re-synchronization is in progress",
            type: Notifications.NotificationType.Success
        }));
    },
};

const initState: TenantState = {
    region: "",
    id: "",
    url: "",
    isReadOnly: false,
    isDemoDataApplied: false,
    name: "",
    externalId: "",
    companyName: "",
    isProcessing: false,
    isLoading: false,
    reporting: {
        odataUrl: "",
        portfolioReports: { packs: [], subPacks: [] },
        portfoliosReports: { packs: [], subPacks: [] },
        programReports: { packs: [], subPacks: [] },
        programsReports: { packs: [], subPacks: [] },
        projectReports: { packs: [], subPacks: [] },
        projectsReports: { packs: [], subPacks: [] },
        resourceReports: { packs: [], subPacks: [] },
        resourcesReports: { packs: [], subPacks: [] },
        challengesReports: { packs: [], subPacks: [] },
        objectivesReports: { packs: [], subPacks: [] }
    },
    insights: {
        portfolioWarningsCalculationDisabled: false,
        projectWarningsCalculationDisabled: false,
        programWarningsCalculationDisabled: false,
        keyDate: {
            statusCalculation: StatusCalculationTypes.Auto,
            thresholds: { minMax: { min: null, max: null } }
        },
        task: {
            statusCalculation: StatusCalculationTypes.Auto,
            thresholds: { minMax: { min: null, max: null } }
        },
        keyResult: {
            statusCalculation: StatusCalculationTypes.Auto,
            thresholds: { minMax: { min: null, max: null } }
        },
        project: {
            statusCalculation: StatusCalculationTypes.AutoManual,
            thresholds: {
                costOverrun: { min: null, max: null },
                lateKeyDates: { min: null, max: null },
                lateTasks: { min: null, max: null },
                overdueIssues: { min: null, max: null },
                overdueRisks: { min: null, max: null },
                resourcesWithLateTasks: { min: null, max: null },
                effortsOverrun: { min: null, max: null },
                durationOverrun: { min: null, max: null }
            }
        },
        portfolio: {
            statusCalculation: StatusCalculationTypes.AutoManual,
            thresholds: {
                costOverrun: { min: null, max: null },
                lateKeyDates: { min: null, max: null }
            }
        },
        program: {
            statusCalculation: StatusCalculationTypes.AutoManual,
            thresholds: {
                costOverrun: { min: null, max: null },
                lateKeyDates: { min: null, max: null }
            }
        }
    },
    aiInsights: { type: AIType.Ppmx, temperature: 0 },
    subscription: {
        endDate: "",
        productType: ProductType.Core,
        paymentType: PaymentType.Card,
        paymentPeriod: PaymentPeriod.Month,
        isEnterprise: false,
        status: SubscriptionStatus.Disabled,
        dataSyncCountLimit: 0,
        plan: "",
        gracePeriodDuration: 0,
        allFeatures: [PPMFeatures.None],
        features: [PPMFeatures.None],
        integrations: PPMIntegrations.None,
        isTrial: false,
        viewersLimit: 0
    },
    styleSettings: {
        logoId: undefined,
        themeType: ThemeType.Light,
    },
    timeTracking: {
        administrativeCategories: [],
        globalSettings: {
            minReportingDurationMinutes: 15,
            reportingIncrementMinutes: 5,
            newTimeTrackingEnabled: true,
            calculateCompletedWorkBasedOnReportedTime: false,
            calculateResourcePlanActualsBasedOnReportedTime: false,
            costTypeSource: ServerEntityType.Task,
            billableSource: ServerEntityType.Task,
            enableEditingBillable: true,
            enableEditingCostType: true,
            enabledAssignmentSmartSuggestions: true,
            enablaResourcePlanSmartSuggestion: false,
            enableO365Connector: true,
            enableReportingOnCustomTimeTrackingEntries: true,
            defaultCustomTimeEntryName: "",
            enableReportingOnNotAssignedTasks: true,
            enableExternalSuggestions: false,
            timesheetApprovalsEnabled: false,
            syncOnlyApprovedTime: false,
            allowFutureTimeSubmission: true,
            allowManagersToRecallApprovedRequests: false,
            delegationModeEnabled: false
        },
        approvalReminderSettings: {
            approverReminderFrequency: ReminderFrequency.DoNotSend,
            overdueApprovalReminderFrequency: ReminderFrequency.DoNotSend,
            approvalRequestOverdueThresholdDays: 1,
            timesheetSubmissionReminderFrequency: ReminderFrequency.DoNotSend,
            timesheetSubmissionReminderStartDays: 3,
            overdueTimesheetReminderFrequency: ReminderFrequency.DoNotSend,
            timesheetOverdueThresholdDays: 1,
            schedule: {
                days: [],
                times: []
            }
        }
    },
    storage: {
        capacity: 0,
        usage: 0,
        maxFileSize: 0,
        usageWarningPercent: 95
    }
}

export const reducer: Reducer<TenantState> = (state: TenantState = initState, incomingAction: Action) => {
    const action = incomingAction as TenantActions;
    switch (action.type) {
        case ActionTypes.REGISTER_TENANT: return {
            ...state,
            isProcessing: true
        }
        case ActionTypes.UPDATE_TENANT: return {
            region: action.tenant.region,
            id: action.tenant.id,
            isReadOnly: action.tenant.isReadOnly,
            isDemoDataApplied: action.tenant.isDemoDataApplied,
            name: action.tenant.name,
            url: action.tenant.url,
            externalId: action.tenant.externalId,
            externalAuthProvider: action.tenant.externalAuthProvider,
            companyName: action.tenant.companyName,
            styleSettings: action.tenant.styleSettings,
            globalSettings: action.tenant.globalSettings,
            syncSettings: action.tenant.syncSettings,
            adSyncSettings: action.tenant.adSyncSettings,
            adUsersSyncSettings: action.tenant.adUsersSyncSettings,
            defaultProjectSettings: action.tenant.defaultProjectSettings,
            presentationModeSettings: action.tenant.presentationModeSettings,
            healthReportingSettings: action.tenant.healthReportingSettings,
            resourcePlanningSettings: action.tenant.resourcePlanningSettings,
            subscription: action.tenant.subscription,
            security: action.tenant.security,
            reporting: action.tenant.reporting,
            insights: action.tenant.insights,
            aiInsights: action.tenant.aiInsights,
            timeTracking: action.tenant.timeTracking,
            storage: action.tenant.storage,
            isLoading: false,
            isProcessing: false
        }
        case ActionTypes.UPDATING_TENANT: return {
            ...state,
            isProcessing: action.isProcessing ?? true
        }
        case ActionTypes.LINK_TENANT: return {
            ...state,
            isProcessing: false,
            externalId: action.externalId,
            externalAuthProvider: action.externalAuthProvider
        }
        case ActionTypes.UPDATE_SCHEDULE: return {
            ...state,
            isProcessing: false,
            syncSettings: {
                schedule: action.syncSchedule,
                alertDelay: action.alertDelay,
                alertRecipientIds: action.alertRecipientIds
            },
            healthReportingSettings: action.notificationSchedule
        }
        case ActionTypes.UPDATE_AD_SYNC_SETTINGS: return {
            ...state,
            isProcessing: false,
            adSyncSettings: {
                lastSyncErrorMessage: action.adSyncSettings.lastSyncErrorMessage,
                lastSyncTime: action.adSyncSettings.lastSyncTime,
                schedule: action.adSyncSettings.schedule,
                organization: action.adSyncSettings.organization,
                mapping: action.adSyncSettings.mapping
            },
        }
        case ActionTypes.UPDATE_AD_USERS_SYNC_SETTINGS: return {
            ...state,
            isProcessing: false,
            adUsersSyncSettings: {
                isADUsersSyncEnabled: action.adUsersSyncSettings.isADUsersSyncEnabled,
                lastSyncErrorMessage: action.adUsersSyncSettings.lastSyncErrorMessage,
                lastSyncTime: action.adUsersSyncSettings.lastSyncTime,
                schedule: action.adUsersSyncSettings.schedule,
                organization: action.adUsersSyncSettings.organization,
                usersGroup: action.adUsersSyncSettings.usersGroup,
                teamMembersGroup: action.adUsersSyncSettings.teamMembersGroup,
                syncType: action.adUsersSyncSettings.syncType
            },
        }
        case ActionTypes.UPDATE_DEFAULT_PROJECT_SETTINGS: return {
            ...state,
            isProcessing: false,
            defaultProjectSettings: {
                progressCalculationType: action.defaultProjectSettings.progressCalculationType,
                taskType: action.defaultProjectSettings.taskType,
                effortDriven: action.defaultProjectSettings.effortDriven,
                calculateStartFinishDate: action.defaultProjectSettings.calculateStartFinishDate,
                updateProgress: action.defaultProjectSettings.updateProgress,
                shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay:
                    action.defaultProjectSettings.shiftZeroDayMilestoneWithFSPredecessorToNextWorkingDay,
            }
        }
        case ActionTypes.UPDATE_PRESENTATION_MODE_SETTINGS: return {
            ...state,
            isProcessing: false,
            presentationModeSettings: {
                project: action.presentationModeSettings.project,
                sourceType: action.presentationModeSettings.sourceType,
            }
        }
        case ActionTypes.UPDATE_SECURITY: return {
            ...state,
            isProcessing: false,
            security: action.security
        }
        case ActionTypes.UPDATE_IS_DEMO_DATA_APPLIED: return {
            ...state,
            isProcessing: false,
            isDemoDataApplied: action.isDemoDataApplied
        }
        case ActionTypes.UPDATE_SETTINGS:
            {
                cultureInfo.name = action.globalSettings.cultureName;
                return {
                    ...state,
                    isProcessing: false,
                    globalSettings: action.globalSettings
                }
            }
        case ActionTypes.UPDATE_RESOURCE_PLANNING_SETTINGS: return {
            ...state,
            isProcessing: false,
            resourcePlanningSettings: action.resourcePlanningSettings
        }
        case ActionTypes.UPDATE_SUBSCRIPTION: return {
            ...state,
            subscription: action.subscription
        }
        case ActionTypes.RECEIVED_REPORTING_INFO: return {
            ...state,
            reporting: action.info
        }
        case ActionTypes.UPDATE_INSIGHTS: return {
            ...state,
            isProcessing: false,
            insights: action.insights
        }
        case ActionTypes.UPDATE_AI_INSIGHTS: return {
            ...state,
            isProcessing: false,
            aiInsights: action.aiInsights
        }
        case ActionTypes.BEGIN_LOADING: return {
            ...state,
            isLoading: true
        }
        case ActionTypes.DEFAULT_PERMISSIONS_LOADED: return {
            ...state,
            isLoading: false,
            isProcessing: false,
            defaultPermissions: {
                regular: toPermissions(action.permissions.regular),
                viewer: toPermissions(action.permissions.viewer)
            }
        }
        case ActionTypes.DEFAULT_PRESENTATION_MODE_LOADED: return {
            ...state,
            isLoading: false,
            isProcessing: false,
            defaultPresentationModeSettings: {
                regularPresentationMode: action.settings.regularPresentationMode,
                viewerPresentationMode: action.settings.viewerPresentationMode
            }
        }
        case ActionTypes.UPDATE_LOGO: return {
            ...state,
            styleSettings: {
                ...state.styleSettings,
                logoId: action.logoId,
            }
        }
        case ActionTypes.SET_THEME: return {
            ...state,
            styleSettings: {
                ...state.styleSettings,
                themeType: action.themeType,
            }
        }
        case ActionTypes.UPDATE_TIMETRACKING_GLOBAL_SETTINGS:

            return {
                ...state,
                isLoading: false,
                isProcessing: false,
                timeTracking: {
                    ...state.timeTracking,
                    globalSettings: action.settings
                }
            };
        case ActionTypes.UPDATE_TIMETRACKING_ADMINISTRATIVE_CATEGORY:
            return {
                ...state,
                isLoading: false,
                isProcessing: false,
                timeTracking: {
                    ...state.timeTracking!,
                    administrativeCategories: addOrUpdateOrRemove(
                        state.timeTracking!.administrativeCategories,
                        action.addOrUpdate,
                        action.remove
                    )
                }
            };
        case ActionTypes.UPDATE_TIMETRACKING_APPROVAL_REMINDER_SETTINGS:
            return {
                ...state,
                isLoading: false,
                isProcessing: false,
                timeTracking: {
                    ...state.timeTracking,
                    approvalReminderSettings: action.settings
                }
            };
        case ActionTypes.UPDATE_STORAGE_INFO:
            return {
                ...state,
                storage: action.storage
            }
        default:
            const exhaustiveCheck: never = action;
    }

    return state;
};